import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import QuestionCard from "@/components/QuestionCard";
import { Progress } from "@/components/ui/progress";
import { CheckCircle2 } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";

export default function Assessment() {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, question: null, progress: 0, index: 0, total: 0 });
  const [answer, setAnswer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const fetchCurrent = useCallback(async () => {
    const res = await api.get("/student/assessment/current");
    if (res.data.idle_restarted) {
      toast.info("Assessment restarted", {
        description: "You were inactive for more than 15 minutes, so your answers were cleared and the assessment restarted from the beginning to keep results accurate.",
      });
    }
    if (res.data.completed) {
      try {
        await api.post("/student/assessment/submit");
      } catch {
        // report generation continues in the background even if this request times out
      }
      setDone(true);
      return;
    }
    setState({ loading: false, question: res.data.question, progress: res.data.progress_percent, index: res.data.index, total: res.data.total });
    setAnswer(null);
  }, []);

  useEffect(() => { fetchCurrent(); }, [fetchCurrent]);

  const handleNext = async () => {
    if (answer === null) return;
    setSaving(true);
    try {
      const res = await api.post("/student/assessment/answer", { question_id: state.question.id, answer });
      if (res.data.restarted) {
        toast.info("Assessment restarted", { description: res.data.message });
      }
      await fetchCurrent();
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6" data-testid="assessment-submitted-view">
        <div className="max-w-md text-center bg-white rounded-2xl border border-slate-200 p-10">
          <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto mb-6" />
          <h1 className="font-heading text-2xl font-bold text-slate-900 mb-3">Assessment Successfully Completed</h1>
          <p className="text-slate-500 mb-8">
            Your responses have been successfully captured and are now being evaluated. CareerStudy360 is preparing
            your detailed personality, strengths and career-alignment report. Please return to your dashboard shortly.
          </p>
          <button data-testid="assessment-back-to-dashboard-button" onClick={() => navigate("/dashboard")} className="text-indigo-600 font-medium hover:underline">
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (state.loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading your assessment…</div>;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="assessment-page">
      <header className="border-b border-slate-200 bg-white sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-6 h-16 flex items-center gap-4">
          <div className="shrink-0">
            <BrandLogo className="h-8" />
          </div>
          <div className="flex-1">
            <Progress value={state.progress} className="h-2" data-testid="assessment-progress-bar" />
          </div>
          <span className="text-sm font-medium text-slate-500 w-14 text-right" data-testid="assessment-progress-percent">{state.progress}%</span>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-6 py-16">
        <QuestionCard
          question={state.question}
          value={answer}
          onChange={setAnswer}
          onNext={handleNext}
          saving={saving}
          isLast={state.index === state.total - 1}
        />
      </main>
    </div>
  );
}
